import React, { useEffect, useState } from 'react';
import { CalendarDays, Star, Globe, PhoneCall } from 'lucide-react';
import { WEEK_LABEL, BRAND, CONTACT_EMAIL, PRIVACY_URL, HOST_PHOTO_URL, AVATAR_URLS, BADGE_LEFT, BADGE_RIGHT, HEADER_TITLE, HEADER_LOGO_URL } from './config';

// PALETA BLOQUEADA: vino #9B1B42 · dorado #C9A44C · rosa claro #FBEEF2
const WINE = '#9B1B42';
const GOLD = '#C9A44C';
export function Wrapper({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`mx-auto w-full max-w-[1080px] px-5 sm:px-8 ${className}`} data-id="element-635" data-mp="4uwavz1c2gw6">{children}</div>;
}

/* Clases tipográficas compartidas */
export const H1 = 'font-extrabold text-ink tracking-tight leading-[1.08] text-[32px] sm:text-[48px] md:text-[60px]';
export const H2 = 'text-center font-extrabold text-ink tracking-tight leading-[1.1] text-[28px] sm:text-[40px] md:text-[48px]';
export const LEAD = 'text-[17px] sm:text-[21px] leading-relaxed text-gray-600';
export const CARD = 'rounded-2xl border border-gray-100 bg-white shadow-[0_10px_30px_-12px_rgba(11,13,18,0.12)]';

/* ---------- Header ---------- */
export function HeaderBar() {
  return <header className="bg-brand" data-id="element-636" data-mp="1g7ejfg9tgf5i">
      <div className="py-3 sm:py-4 flex items-center justify-center gap-2.5 text-white" data-id="element-637" data-mp="1b78pftg2qgk9">
        {HEADER_LOGO_URL ? <img src={HEADER_LOGO_URL} alt="logo" height={36} decoding="async" className="h-7 sm:h-9 w-auto" data-id="element-638" data-mp="176gfexzdo7mx" /> : <PhoneCall className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2} data-id="element-639" data-mp="127c7eao6wfy6" />}
        <span className="font-semibold text-[16px] sm:text-[19px] tracking-tight" data-id="element-640" data-mp="128ncqexa9vre">{HEADER_TITLE}</span>
      </div>
    </header>;
}

/* ---------- Icons ---------- */
export function TicketIcon({
  className = 'w-14 h-7'
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 120 60" className={className} aria-hidden="true" data-id="element-641" data-mp="nj8x4p346i3k">
      <path d="M8 6h104v14a10 10 0 0 0 0 20v14H8V40a10 10 0 0 0 0-20z" fill="#fff" stroke={GOLD} strokeWidth="6" strokeLinejoin="round" data-id="element-642" data-mp="jgm3qjqsop49" />
      <rect x="16" y="14" width="88" height="32" rx="2" fill={GOLD} data-id="element-643" data-mp="ei1effd2zpul" />
      <line x1="88" y1="16" x2="88" y2="44" stroke="#fff" strokeWidth="2.5" strokeDasharray="4 3" data-id="element-644" data-mp="4skowqttqaed" />
      <text x="52" y="36" textAnchor="middle" fontFamily="Inter, Arial, sans-serif" fontWeight="800" fontSize="17" fill="#fff" data-id="element-645" data-mp="8wa9z6bai9un">1:1</text>
    </svg>;
}
export function GreenCheck({
  className = 'w-7 h-7'
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-646" data-mp="3w21bxtc61wx7">
      <circle cx="12" cy="12" r="11" fill={WINE} data-id="element-647" data-mp="nkn9uedi0cuf" />
      <path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-648" data-mp="siykgrbmardp" />
    </svg>;
}
export function PendingDot({
  className = 'w-7 h-7'
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-649" data-mp="is3w9het9hpm">
      <circle cx="12" cy="12" r="10" fill="#fff" stroke={GOLD} strokeWidth="2" strokeDasharray="3 3" data-id="element-650" data-mp="16ihfqjjby88l" />
      <circle cx="12" cy="12" r="3" fill={GOLD} data-id="element-651" data-mp="1ba7vj511do7e" />
    </svg>;
}
export function VerifiedSeal({
  className = 'w-6 h-6',
  color = WINE
}: {
  className?: string;
  color?: string;
}) {
  return <svg viewBox="0 0 18 18" className={className} aria-hidden="true" data-id="element-652" data-mp="2r1a5a1l7bsvx">
      <path fillRule="evenodd" clipRule="evenodd" fill={color} d="M8.03.6c.52-.57 1.42-.57 1.94 0l.91 1c.05.05.12.07.2.05l1.28-.41c.74-.23 1.51.22 1.68.97l.29 1.32c.01.07.07.13.14.14l1.32.29c.75.17 1.2.94.97 1.68l-.41 1.28c-.02.07 0 .15.05.2l1 .91c.57.52.57 1.42 0 1.94l-1 .91c-.05.05-.07.12-.05.2l.41 1.28c.23.74-.22 1.51-.97 1.68l-1.32.29a.18.18 0 0 0-.14.14l-.29 1.32c-.17.75-.94 1.2-1.68.97l-1.28-.41a.18.18 0 0 0-.2.05l-.91 1c-.52.57-1.420.57-1.94 0l-.91-1a.18.18 0 0 0-.2-.05l-1.28.41c-.74.23-1.51-.22-1.68-.97l-.29-1.32a.18.18 0 0 0-.14-.14l-1.32-.29c-.75-.17-1.2-.94-.97-1.68l.41-1.28a.18.18 0 0 0-.05-.2l-1-.91c-.57-.52-.57-1.42 0-1.94l1-.91c.05-.05.07-.12.05-.2l-.41-1.28c-.23-.74.22-1.51.97-1.68l1.32-.29c.07-.01.13-.07.14-.14l.29-1.32c.17-.75.94-1.2 1.68-.97l1.28.41c.07.02.15 0 .2-.05zM13.18 6.98c.2-.31.25-.71.03-1-.27-.37-.6-.63-.9-.8-.4-.23-.89-.05-1.16.33-1.64 2.33-2.67 3.85-2.67 3.85s-.61-.77-1.64-1.87c-.35-.38-.93-.46-1.32-.12-.21.19-.44.41-.65.68-.26.33-.2.8.06 1.13 1.01 1.25 1.99 2.28 2.69 2.97.57.56 1.46.51 1.95-.11.76-.95 1.98-2.57 3.61-5.06z" data-id="element-653" data-mp="2qz7e3e8bbtpm" />
    </svg>;
}

// Estrellas: SOLO dentro de testimonios reales
export function Stars({
  size = 18
}: {
  size?: number;
}) {
  return <div className="flex gap-0.5" role="img" aria-label="5 de 5 estrellas" data-id="element-654" data-mp="16hpuzbnzjb3f">
      {Array.from({
      length: 5
    }).map((_, i) => <Star key={i} width={size} height={size} className="fill-star text-star" strokeWidth={1} data-id="element-655" data-mp="1fkh3b2lkdqqu" data-mpi={i} />)}
    </div>;
}
export function VerifiedTag({
  label
}: {
  label: string;
}) {
  return <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-light px-3 py-1 text-[13px] font-semibold text-brand" data-id="element-656" data-mp="1pak6twqban3f">
      <VerifiedSeal className="w-4 h-4" data-id="element-657" data-mp="1uag47d4i7ga0" />
      {label}
    </span>;
}

/* ---------- Countdown: la agenda de esta semana cierra el domingo 11:59 PM ---------- */
function weekEnd() {
  const now = new Date();
  const end = new Date(now);
  end.setDate(now.getDate() + (7 - now.getDay()) % 7);
  end.setHours(23, 59, 59, 999);
  return end.getTime();
}
function diff() {
  const d = weekEnd() - Date.now();
  if (isNaN(d) || d <= 0) return {
    d: 0,
    h: 0,
    m: 0,
    s: 0
  };
  return {
    d: Math.floor(d / 86400000),
    h: Math.floor(d % 86400000 / 3600000),
    m: Math.floor(d % 3600000 / 60000),
    s: Math.floor(d % 60000 / 1000)
  };
}
export function Countdown() {
  const [t, setT] = useState(diff());
  useEffect(() => {
    const i = setInterval(() => setT(diff()), 1000);
    return () => clearInterval(i);
  }, []);
  const items: [number, string][] = [[t.d, 'Días'], [t.h, 'Horas'], [t.m, 'Min'], [t.s, 'Seg']];
  return <div className="flex items-center justify-center gap-2 sm:gap-3" role="timer" aria-live="off" data-id="element-658" data-mp="3nrkpfbm3mown">
      {items.map(([v, l]) => <div key={l} className="w-[70px] sm:w-[100px] rounded-2xl border border-gray-100 bg-white shadow-[0_8px_24px_-12px_rgba(11,13,18,0.18)] py-3 sm:py-4 flex flex-col items-center" data-id="element-659" data-mp="p46ppqrbvvyw">
          <span className="text-[30px] sm:text-[44px] font-extrabold leading-none text-brand tabular-nums" data-id="element-660" data-mp="2ekam2wcjngad">{String(v).padStart(2, '0')}</span>
          <small className="mt-1.5 text-[11px] sm:text-[13px] font-semibold uppercase tracking-wide text-gray-500" data-id="element-661" data-mp="24u7mueetx63l">{l}</small>
        </div>)}
    </div>;
}
export function ScheduleCountdown() {
  return <div className="mx-auto mt-12 max-w-[640px]" data-id="element-662" data-mp="1uv8llgwe8cqm">
      <div className="flex items-center justify-center gap-2 text-brand mb-4" data-id="element-663" data-mp="1uxmhtt2t7uzr">
        <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" strokeWidth={2} data-id="element-664" data-mp="1zw611lq0qsof" />
        <span className="font-bold text-[16px] sm:text-[20px] leading-tight" data-id="element-665" data-mp="1lw52we151zcn">La agenda de esta semana cierra en:</span>
      </div>
      <Countdown data-id="element-666" data-mp="1qw0wdjc7m8dy" />
    </div>;
}

/* ---------- Host row ---------- */
function StatBadge({
  title,
  value
}: {
  title: string;
  value: string;
}) {
  return <div className="flex flex-col items-center gap-1.5 w-[84px] sm:w-[120px]" data-id="element-667" data-mp="3l6wfvgoxn7vi">
      <span className="text-[12px] sm:text-[14px] font-semibold uppercase tracking-wide text-gray-500" data-id="element-668" data-mp="3vbcu65mrkgx7">{title}</span>
      <div className="w-full rounded-2xl bg-brand-light py-2.5 sm:py-4 text-center font-extrabold text-brand text-[20px] sm:text-[30px] leading-none" data-id="element-669" data-mp="3qduja9p6rrmk">{value}</div>
    </div>;
}
export function HostRow() {
  return <div className="flex items-end justify-center gap-4 sm:gap-10 mt-12" data-id="element-670" data-mp="36iqa717f3qw">
      <StatBadge {...BADGE_LEFT} data-id="element-671" data-mp="hw6rl89dy2em" />
      {HOST_PHOTO_URL ? <div className="h-[200px] sm:h-[320px] w-[144px] sm:w-[230px] shrink-0 overflow-hidden rounded-t-full bg-brand-light shadow-[0_18px_40px_-18px_rgba(11,13,18,0.35)] ring-4 ring-white" data-id="element-672" data-mp="cxviizqwzvxg">
          <img src={HOST_PHOTO_URL} alt="Chale Rodriguez" width={230} height={320} loading="lazy" decoding="async" className="h-full w-full object-cover object-[50%_30%]" data-id="element-673" data-mp="qvboka5atnoo" />
        </div> : <div className="h-[200px] sm:h-[320px] w-[140px] sm:w-[230px] rounded-t-full bg-gradient-to-b from-brand-light to-[#F6D7E1] flex items-center justify-center text-xs font-semibold text-brand/70" data-id="element-674" data-mp="2squgwobwik47">[FOTO CHALE]</div>}
      <StatBadge {...BADGE_RIGHT} data-id="element-675" data-mp="3i6xiv9ryy95u" />
    </div>;
}

/* ---------- Social proof ---------- */
export function TicketLine() {
  return <p className="flex items-center justify-center gap-2.5 text-center text-[16px] sm:text-[22px] font-bold text-brand leading-tight" data-id="element-676" data-mp="3mzgzkflydkev">
      <TicketIcon className="w-9 h-[18px] sm:w-12 sm:h-6 shrink-0" data-id="element-677" data-mp="3r1lmkvhwbisj" />
      <span data-id="element-678" data-mp="2ub2z7t7e6c05">Tu llamada 1:1 con Chale es gratis</span>
    </p>;
}
const AVATAR_BG = ['#F3D9E1', '#EAD9C8', '#E9CBD5', '#F1E3D6', '#E6C9D2', '#EBDCCB', '#F2D4DE', '#E7D3C1', '#EDCFD9', '#F0DFD0'];

// Sin estrellas aquí: los avatares son de la comunidad, no reseñas
export function TrustStrip({
  withTicket = true
}: {
  withTicket?: boolean;
}) {
  const avatars = AVATAR_URLS.length ? AVATAR_URLS.slice(0, 10) : AVATAR_BG;
  return <div className="flex flex-col items-center gap-4 mt-10" data-id="element-679" data-mp="imbsoao5tjvu">
      {withTicket && <TicketLine data-id="element-680" data-mp="nh7l3bxb699h" />}
      <div className="flex -space-x-2.5" data-id="element-681" data-mp="nkvapzzmubix">
        {avatars.map((a, i) => AVATAR_URLS.length ? <img key={i} src={a} alt="" width={44} height={44} loading="lazy" decoding="async" className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full object-cover ring-2 ring-white ${i > 6 ? 'hidden sm:block' : ''}`} data-id="element-682" data-mp="xotqflnbwu9j" /> : <div key={i} style={{
        background: a
      }} className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full ring-2 ring-white ${i > 6 ? 'hidden sm:block' : ''}`} data-id="element-683" data-mp="jw423akbvd99" />)}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 text-[14px] sm:text-[16px] font-medium text-gray-700" data-id="element-684" data-mp="9xegp7t83ao9">
        <VerifiedSeal className="w-5 h-5" data-id="element-685" data-mp="4xs5t50va6im" />
        <span data-id="element-686" data-mp="4ylc64vwyu08">Únete a la comunidad que está construyendo algo propio</span>
      </div>
    </div>;
}

/* ---------- CTA ---------- */
export function CtaButton({
  onClick,
  type = 'button',
  form,
  label,
  sub,
  disabled
}: {
  onClick?: () => void;
  type?: 'button' | 'submit';
  form?: string;
  label?: string;
  sub?: string;
  disabled?: boolean;
}) {
  return <button type={type} form={form} onClick={onClick} disabled={disabled} className="cta-btn w-full max-w-[620px] mx-auto flex flex-col items-center rounded-full bg-brand hover:bg-brand-dark disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-4 sm:py-5" data-id="element-687" data-mp="uls0tntknzpn">
      <span className="text-[18px] sm:text-[24px] font-bold leading-tight" data-id="element-688" data-mp="zleetzh85jzi">{label || 'Sí, quiero mi llamada con Chale'}</span>
      <small className="mt-0.5 text-[13px] sm:text-[15px] font-medium text-white/80 leading-tight" data-id="element-689" data-mp="bv1zw4nkamzf">{sub || 'Cupos limitados esta semana'}</small>
    </button>;
}

/* ---------- Footer ---------- */
export function Footer() {
  return <footer className="bg-brand py-12 text-[12px] sm:text-[13px] text-white/85 leading-relaxed" data-id="element-690" data-mp="lmxydgtk8f46">
      <Wrapper className="max-w-[860px] space-y-4 text-center" data-id="element-691" data-mp="gtbsq8ltdbhv">
        <p className="font-bold text-white" data-id="element-692" data-mp="3p9y9d5klme83">Aviso legal y de ingresos</p>
        <p data-id="element-693" data-mp="3kbn478590rw7">{BRAND} es representante independiente de Primerica. Esto no es una oferta de empleo. Los ingresos no están garantizados y dependen del esfuerzo, la actividad y las habilidades de cada persona. Las comisiones se pagan por la venta de productos, no por reclutar. La incorporación está sujeta a requisitos de licencia, capacitación y aprobación.</p>
        <p data-id="element-694" data-mp="1quh8zmu12xja">Este sitio no forma parte del sitio web de Facebook ni de Instagram, ni está respaldado por Meta, Inc., X, TikTok, LinkedIn ni ninguna otra plataforma de redes sociales.</p>
        <p data-id="element-695" data-mp="2ildpqyj7lmbu">¿Preguntas? Escríbenos a <a className="font-semibold text-white underline underline-offset-2" href={`mailto:${CONTACT_EMAIL}`} data-id="element-696" data-mp="2ihzm7myt772t">{CONTACT_EMAIL}</a></p>
        <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2 font-medium text-white" data-id="element-697" data-mp="29jnwro9oifby">
          <li data-id="element-698" data-mp="2ei8o0y6ydoo9"><a href={PRIVACY_URL} className="underline-offset-2 hover:underline" data-id="element-699" data-mp="2ei8pl2mlpwqd">Política de Privacidad</a></li>
          <li data-id="element-700" data-mp="y3pj173k354e"><a href="#" className="underline-offset-2 hover:underline" data-id="element-701" data-mp="y3zbd1c2pod9">Términos de Servicio</a></li>
        </ul>
        <p className="text-white/70" data-id="element-702" data-mp="134d8hoc4zdwi">© 2026 {BRAND}. Todos los derechos reservados.</p>
      </Wrapper>
    </footer>;
}
export { Globe, WEEK_LABEL };